import { trace } from "../utils/logging";
import { DICT_DEFAULT_RESOURCE_TEXT, HIERARCHY_EVENTS } from "../utils/constants"
import CategorySectionComponent from "./category-section.component";
import DropdownComponent from "./dropdown/dropdown.component";

const ROOT_LEVEL = 2; //Dont ask me, its what the server is sending over, probably due to using the tagging component whereby 1 is the initial root node which contains all categories

export default class HierarchyAppointmentComponent extends HTMLElement {

    #resourcesService = null;

    get allCategorySections() {
        return Array.from(this.querySelectorAll(`vee24-scheduler-category-section`));
    }

    constructor() {
        super();       
    }

    init(categories, resourcesService) {

        this.#resourcesService = resourcesService;

        this.categories = categories;
        this.selectedCategory = null;

        const minNumberOfOptions = this.#resourcesService.getResource("SELFSCHEDULER_HIERARCHY_TYPEAHEAD_MIN_LIMIT", 5);
        const shouldUseTypeahead = (items) => (items.length > minNumberOfOptions && minNumberOfOptions != -1);

        //check to see if we have a custom resource for the selected category, else use default resource
        const getCategoryLabel = (name) => {
            const labelResourceKey = `SELFSCHEDULER_SELECTCATEGORY_${name?.replace(/\s/g, "_").toUpperCase()}`;
            return this.#resourcesService.getResource(labelResourceKey, this.#resourcesService.getResource("SELFSCHEDULER_SELECTCATEGORY", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_SELECTCATEGORY));
        };

        const setupLinkedCategorySection = (nextCategorySection, category) => {

            const linkedDropdown = nextCategorySection.querySelector(`vee24-scheduler-dropdown`);

            if (!linkedDropdown) {
                trace("linked dropdown not found for", nextCategorySection);
                return;
            }

            if (!category) {
                trace("category is undefined");
                return;
            }

            const translatedOptions = category.childCategories.map(option => ({
                ...option,
                name: this.#resourcesService.getFormattedResource(option.name, 'SELFSCHEDULER_APPOINTMENT_TYPE', '', option.name)
            }));
            linkedDropdown.setOptions(translatedOptions, shouldUseTypeahead(translatedOptions));

            nextCategorySection.setLabel(getCategoryLabel(category.name));
            nextCategorySection.show(true);

            if (category.childCategories.length === 1) {
                //cant select index in a datalist
                linkedDropdown.setCurrentIndex(1);
            }
        };

        const selectNextItem = (id, level) => {

            const selectedLevel = parseInt(level);

            if (!selectedLevel || isNaN(selectedLevel)) {
                console.warn("level is not defined");
                return;
            }

            this.hideCategorySectionsFrom(selectedLevel);

            if (this.selectedCategory) {
              this.selectedCategory = null;
              this.dispatchEvent(
                new CustomEvent(HIERARCHY_EVENTS.categoryDeSelected, {
                  bubbles: true,
                })
              );
            }

            if (id === "-1") {
                return;
            }

            const category = this.categories?.findCategoryById(id);
            if (!category) {
                console.warn("category not found");
                trace(id, this.categories.categories);
                return;
            }

            if (category.childCategories.length === 0) {

                this.selectedCategory = category;

                this.dispatchEvent(new CustomEvent(HIERARCHY_EVENTS.categorySelected, {
                    bubbles: true,
                    detail: {
                        category: this.selectedCategory,
                        preSelectedCategory: this.preSelectedCategory,
                    }
                }));

                trace("no more children, select category");
                return;
            }

            const nextLevel = selectedLevel + 1;
            const nextCategorySection = this.querySelector(`#category-${nextLevel}`);

            if (!nextCategorySection) {
                trace("no linked category section");
                return;
            }

            setupLinkedCategorySection(nextCategorySection, category);
        }

        const onSelectElementChange = (e) => {
            const target = e.target;
            const {selectedId, level} = target.dataset;
            selectNextItem(selectedId, level);
        };

        const createCategoryDropdowns = (dropdowns) => dropdowns.forEach((dropdown, index) => this.appendChild(createCategoryDropdown(dropdown, index + ROOT_LEVEL)));

        const createCategoryDropdown = (data, level) => {

            const optionsData = this.preSelectedCategory || ROOT_LEVEL === level ? data : [];

            const translatedOptions = optionsData.map(option => ({
                ...option,
                name: this.#resourcesService.getFormattedResource(option.name, 'SELFSCHEDULER_APPOINTMENT_TYPE', '', option.name)
            }));

            const placeholder = this.#resourcesService.getResource("SELFSCHEDULER_DEFAULTSELECTOPTION", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_DEFAULTSELECTOPTION);
            const sortById = this.#resourcesService.getResource("SELFSCHEDULER_DROPDOWN_SORTBYID", "0") === "1";

            const dropdownEl = new DropdownComponent({ className: "v24-select", level, placeholder, sortById });
            dropdownEl.setOptions(translatedOptions, shouldUseTypeahead(translatedOptions));

            const categorySection = new CategorySectionComponent({
                id: `category-${level}`,
                className: "v24-panel-appointment-category",
                style: "margin:auto;!important",
                index: level,
            });

            const labelResource = this.#resourcesService.getResource("SELFSCHEDULER_SELECTCATEGORY", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_SELECTCATEGORY);

            categorySection.setLabel(labelResource, "scheduler-customerinfo-label");
            categorySection.appendChild(dropdownEl);
            categorySection.show(this.preSelectedCategory ? false : optionsData.length > 0);
            categorySection.addEventListener("dropdown-change", onSelectElementChange);

            return categorySection;
        };

        this.render = (options) => {

            this.preSelectedCategory = options?.categoryName ? this.categories.findCategoryByName(options.categoryName) : options?.categoryId ? this.categories.findCategoryById(options.categoryId) : null;

            if ((options?.categoryName || options?.categoryId) && !this.preSelectedCategory) {
                console.warn(`Unable to preselect category.`);
                this.dispatchEvent(new CustomEvent(HIERARCHY_EVENTS.categoryNotFound, {bubbles: true }));
            }

            createCategoryDropdowns(Object.values(this.categories.getCategoriesByLevel()));

            if (this.preSelectedCategory) {
                const categorySection = this.querySelector(`#category-${this.preSelectedCategory.level}`);
                const dropdown = categorySection?.querySelector("vee24-scheduler-dropdown");
                if (dropdown) {
                    dropdown.setSelectedId(this.preSelectedCategory.id);
                }
            }
            
        };

        this.dispose = () => {
            this.selectedCategory = null;
            this.allCategorySections.forEach(categorySection => categorySection.removeEventListener("dropdown-change", onSelectElementChange));
        };
    }

    hideCategorySectionsFrom(currentLevel) {
        const categorySections = this.allCategorySections.filter(categorySection => categorySection.index > currentLevel);
        categorySections?.forEach(categorySection => categorySection.show(false));
    }


}